<template>
  <div ref="root" class="labeled-icon">
    <i
      :class="icon"
      :aria-label="ariaLabel"
      :aria-hidden="ariaLabel ? 'false' : 'true'"
    />

    <slot name="label" :label="label">
      <div
        ref="text"
        v-tooltip="{
          value: label,
          disabled: !isTextRefOverflowing,
        }"
        class="labeled-icon-text"
      >
        {{ label }}
      </div>
    </slot>
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue';
import {
  computed,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  useAttrs,
  useTemplateRef,
  watch,
} from 'vue';

import { useTooltip } from '@rygen/rygen-ui/composables';

const props = defineProps({
  icon: {
    type: String as PropType<`pi ${string}`>,
    required: true,
  },
  label: {
    type: String,
    default: () => '',
  },
});
const { icon, label } = toRefs(props);

defineSlots<{
  label?: (props: { label: string }) => any;
}>();

const root = useTemplateRef('root');
const textRef = useTemplateRef('text');

const attrs = useAttrs();
const vTooltip = useTooltip();

const textRefWidths = ref({ offset: 0, scroll: 0 });

const resizeObserver = new ResizeObserver(() => {
  if (textRef.value) {
    textRefWidths.value.offset = textRef.value.offsetWidth;
    textRefWidths.value.scroll = textRef.value.scrollWidth;
  }
});

const ariaLabel = computed(() => attrs.ariaLabel as string | undefined);

const isTextRefOverflowing = computed(() => {
  return textRefWidths.value.offset < textRefWidths.value.scroll;
});

watch(label, () => {
  if (root.value) {
    resizeObserver.unobserve(root.value);
    resizeObserver.observe(root.value);
  }
});

onMounted(() => {
  if (root.value) {
    resizeObserver.observe(root.value);
  }
});

onUnmounted(() => {
  if (root.value) {
    resizeObserver.unobserve(root.value);
  }
});
</script>

<style>
.labeled-icon {
  display: flex;
  gap: var(--sp-2);
  align-items: center;

  .labeled-icon-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
